/**
 * Color conversion utilities for handling HSL and Hex color formats.
 * The platform stores colors in HSL format for CSS custom property compatibility.
 */

/**
 * Convert HSL string to Hex color
 * @param hsl - HSL string in format "H S% L%" (e.g., "221 83% 53%")
 * @returns Hex color string (e.g., "#3b82f6")
 */
export function hslToHex(hsl: string): string {
  if (!hsl || typeof hsl !== 'string') {
    return '#3b82f6'; // Default blue fallback
  }

  // Handle case where hsl might already be hex
  if (hsl.startsWith('#')) {
    return hsl;
  }

  const parts = hsl.split(' ').map(p => parseFloat(p));
  if (parts.length !== 3 || parts.some(isNaN)) {
    return '#3b82f6'; // Default blue fallback
  }

  const h = parts[0];
  const s = parts[1] / 100;
  const l = parts[2] / 100;

  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs((h / 60) % 2 - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;
  if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
  else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
  else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
  else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
  else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const toHex = (n: number) => 
    Math.round((n + m) * 255).toString(16).padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Convert Hex color to HSL string
 * @param hex - Hex color string (e.g., "#3b82f6" or "3b82f6")
 * @returns HSL string in format "H S% L%" (e.g., "221 83% 53%")
 */
export function hexToHsl(hex: string): string {
  if (!hex || typeof hex !== 'string') {
    return '221 83% 53%'; // Default blue fallback
  }

  // If already in HSL format, return as-is
  if (isHslFormat(hex)) {
    return hex;
  }

  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return '221 83% 53%'; // Default blue fallback
  }

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: h = ((b - r) / d + 2) * 60; break;
      case b: h = ((r - g) / d + 4) * 60; break;
    }
  }

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Check if a color string is in HSL format
 * Format: "H S% L%" (e.g., "221 83% 53%")
 */
export function isHslFormat(color: string): boolean {
  if (!color || typeof color !== 'string') return false;
  return /^\d{1,3}\s+\d{1,3}%\s+\d{1,3}%$/.test(color.trim());
}

/**
 * Check if a color string is in Hex format
 * Format: "#RRGGBB" or "RRGGBB"
 */
export function isHexFormat(color: string): boolean {
  if (!color || typeof color !== 'string') return false;
  return /^#?[a-f\d]{6}$/i.test(color.trim());
}

/**
 * Normalize any color format to the desired output format
 * @param color - Input color in either HSL or Hex format
 * @param format - Desired output format ('hsl' or 'hex')
 * @returns Color string in the desired format
 */
export function normalizeColor(color: string, format: 'hsl' | 'hex'): string {
  if (!color) {
    return format === 'hsl' ? '221 83% 53%' : '#3b82f6';
  }

  const isHsl = isHslFormat(color);
  const isHex = isHexFormat(color);

  if (format === 'hsl') {
    if (isHsl) return color;
    if (isHex) return hexToHsl(color);
    return '221 83% 53%'; // Default fallback
  } else {
    if (isHex) return color.startsWith('#') ? color : `#${color}`;
    if (isHsl) return hslToHex(color);
    return '#3b82f6'; // Default fallback
  }
}

/**
 * Get default brand colors in HSL format
 */
/**
 * Parse a color string (HSL or Hex) to RGB values
 * @returns [r, g, b] where each value is 0-255
 */
function parseColorToRgb(color: string): [number, number, number] {
  if (!color) return [0, 0, 0];
  
  // If hex format, convert directly
  if (isHexFormat(color) || color.startsWith('#')) {
    const hex = color.startsWith('#') ? color : `#${color}`;
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return [
        parseInt(result[1], 16),
        parseInt(result[2], 16),
        parseInt(result[3], 16)
      ];
    }
  }
  
  // If HSL format, convert to RGB
  if (isHslFormat(color)) {
    const parts = color.split(' ').map(p => parseFloat(p));
    if (parts.length === 3) {
      const h = parts[0];
      const s = parts[1] / 100;
      const l = parts[2] / 100;
      
      const c = (1 - Math.abs(2 * l - 1)) * s;
      const x = c * (1 - Math.abs((h / 60) % 2 - 1));
      const m = l - c / 2;
      
      let r = 0, g = 0, b = 0;
      if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
      else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
      else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
      else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
      else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
      else { r = c; g = 0; b = x; }
      
      return [
        Math.round((r + m) * 255),
        Math.round((g + m) * 255),
        Math.round((b + m) * 255)
      ];
    }
  }
  
  return [0, 0, 0];
}

/**
 * Calculate relative luminance of a color (0-1 scale)
 * Uses the WCAG formula for perceptual luminance
 * @param color - Color in HSL or Hex format
 * @returns Luminance value between 0 (black) and 1 (white)
 */
export function getRelativeLuminance(color: string): number {
  const [r, g, b] = parseColorToRgb(color);
  
  // Normalize to 0-1
  const rNorm = r / 255;
  const gNorm = g / 255;
  const bNorm = b / 255;
  
  // Apply gamma correction
  const rLinear = rNorm <= 0.03928 ? rNorm / 12.92 : Math.pow((rNorm + 0.055) / 1.055, 2.4);
  const gLinear = gNorm <= 0.03928 ? gNorm / 12.92 : Math.pow((gNorm + 0.055) / 1.055, 2.4);
  const bLinear = bNorm <= 0.03928 ? bNorm / 12.92 : Math.pow((bNorm + 0.055) / 1.055, 2.4);
  
  // Calculate luminance using WCAG coefficients
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Check if a color is considered "light" (luminance > 0.5)
 * @param color - Color in HSL or Hex format
 */
export function isLightColor(color: string): boolean {
  return getRelativeLuminance(color) > 0.5;
}

/**
 * Get a contrasting text color (black or white) for a given background
 * @param backgroundColor - Background color in HSL or Hex format
 * @returns '#0a0a0a' for light backgrounds, '#ffffff' for dark backgrounds
 */
export function getContrastTextColor(backgroundColor: string): string {
  return isLightColor(backgroundColor) ? '#0a0a0a' : '#ffffff';
}

/**
 * Parse an HSL string into its components
 * @param hsl - HSL string in format "H S% L%"
 * @returns [h, s, l] where h is degrees, s and l are percentages
 */
export function parseHsl(hsl: string): [number, number, number] {
  if (!hsl || !isHslFormat(hsl)) {
    return [221, 83, 53]; // Default blue
  }
  const parts = hsl.split(' ').map(p => parseFloat(p));
  return [parts[0], parts[1], parts[2]];
}

/**
 * Convert HSL components back to string
 */
export function hslToString(h: number, s: number, l: number): string {
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
}

/**
 * Lighten an HSL color by increasing lightness
 * @param hsl - HSL string in format "H S% L%"
 * @param amount - Amount to lighten (0-100)
 * @returns Lightened HSL string
 */
export function lightenHsl(hsl: string, amount: number): string {
  const [h, s, l] = parseHsl(hsl);
  const newL = Math.min(100, l + amount);
  return hslToString(h, s, newL);
}

/**
 * Darken an HSL color by decreasing lightness
 * @param hsl - HSL string in format "H S% L%"
 * @param amount - Amount to darken (0-100)
 * @returns Darkened HSL string
 */
export function darkenHsl(hsl: string, amount: number): string {
  const [h, s, l] = parseHsl(hsl);
  const newL = Math.max(0, l - amount);
  return hslToString(h, s, newL);
}

/**
 * Saturate an HSL color by increasing saturation
 * @param hsl - HSL string in format "H S% L%"
 * @param amount - Amount to saturate (0-100)
 * @returns Saturated HSL string
 */
export function saturateHsl(hsl: string, amount: number): string {
  const [h, s, l] = parseHsl(hsl);
  const newS = Math.min(100, s + amount);
  return hslToString(h, newS, l);
}

/**
 * Desaturate an HSL color by decreasing saturation
 * @param hsl - HSL string in format "H S% L%"
 * @param amount - Amount to desaturate (0-100)
 * @returns Desaturated HSL string
 */
export function desaturateHsl(hsl: string, amount: number): string {
  const [h, s, l] = parseHsl(hsl);
  const newS = Math.max(0, s - amount);
  return hslToString(h, newS, l);
}

/**
 * Create a muted version of a color (low saturation, medium lightness)
 */
export function muteHsl(hsl: string): string {
  const [h, s, l] = parseHsl(hsl);
  return hslToString(h, Math.min(s, 20), Math.max(l, 95));
}

/**
 * Shift the hue of an HSL color
 * @param hsl - HSL string in format "H S% L%"
 * @param degrees - Degrees to shift (can be negative)
 * @returns HSL string with shifted hue
 */
export function shiftHue(hsl: string, degrees: number): string {
  const [h, s, l] = parseHsl(hsl);
  const newH = (h + degrees + 360) % 360;
  return hslToString(newH, s, l);
}

export const DEFAULT_BRAND_COLORS = {
  primary: '221 83% 53%',
  secondary: '210 40% 96%',
  accent: '262 83% 58%',
} as const;
